import {
    Injectable, inject
} from "@angular/core";
import {
    Observable, of, catchError
} from "rxjs";
import { map } from "rxjs/operators";

import { StringUtil } from "@shopliftr/common-js/data";

import { IAutocompleteController } from "../../interfaces/autocomplete-controller";
import { IAutocompleteResults } from "../../interfaces/autocomplete-results";
import { IAutocompleteOption } from "../../interfaces/autocomplete-option";
import { ProductApiClientService } from "@shopliftr/common-ng";

/**
 * Autocomplete controller service for manufacturer selection.
 *
 * This service implements the IAutocompleteController interface to provide
 * autocomplete functionality for manufacturer strings using the ProductApiClientService.
 * Unlike enum-based controllers, this service works with dynamic string values
 * retrieved from the manufacturer suggest endpoint.
 */
@Injectable({
    providedIn: "root"
})
export class ManufacturerAutocompleteControllerService implements IAutocompleteController<string> {
    private readonly _productClient = inject(ProductApiClientService);

    /**
     * Gets autocomplete results for manufacturers based on the provided query.
     *
     * @param query - The search query string to filter manufacturers
     * @param options - Additional autocomplete options (currently unused for manufacturers)
     * @returns Observable of autocomplete results containing manufacturer strings
     */
    autocomplete$(query?: string, options?: any): Observable<IAutocompleteResults<string>> {
        // If query is empty or undefined, return empty results (manufacturers require search text)
        if (StringUtil.emptyString(query, true)) {
            return of({
                options: []
            });
        }

        // Call the ProductApiClientService to get manufacturers
        return this._productClient.getManufacturers$(StringUtil.sanitizeString(query)).pipe(
            map((manufacturers: Array<string>) => {
                // Convert manufacturer strings to autocomplete options
                const autocompleteOptions: Array<IAutocompleteOption<string>> = manufacturers.map((manufacturer) => ({
                    option: manufacturer,
                    value: manufacturer
                }));

                return {
                    options: autocompleteOptions
                };
            }),
            catchError(() => {
                // Return empty results on error
                return of({
                    options: []
                });
            })
        );
    }

    /**
     * Gets the display name for a manufacturer value.
     * For manufacturers, the value and display name are the same.
     *
     * @param value - The manufacturer string value
     * @returns The display name for the manufacturer
     */
    getAutocompleteDisplayName(value?: string): string {
        return value || "";
    }

    /**
     * Gets the identifier for tracking purposes in autocomplete lists.
     * Uses the manufacturer string as the unique identifier.
     *
     * @param index - The index of the item in the list
     * @param result - The autocomplete option result
     * @returns The unique identifier for the manufacturer
     */
    trackByIdentifier(index: number, result?: IAutocompleteOption<string>): string | undefined {
        return result?.value;
    }

    /**
     * Checks if a manufacturer value is unique within the provided array.
     *
     * @param value - The manufacturer string to check
     * @param items - Array of existing manufacturer strings to compare against
     * @returns True if the manufacturer is unique, false otherwise
     */
    isUnique(value: string, items?: Array<string>): boolean {
        if (!items) {
            return true;
        }

        return !items.includes(value);
    }
}
